import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { VertexData } from "@babylonjs/core/Meshes/mesh.vertexData";
import { Matrix, Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import { getAssetManager, type InstantiatedModel } from "../../systems/AssetManager";
import { tagAs } from "../PlaceholderTag";

const ASSETS = {
  wheel: { path: "canopy_timing_gear", required: ["CanopyMechanism_wheel_band", "CanopyMechanism_wheel_hub"] },
  supports: { path: "canopy_timing_frame", required: ["CanopyMechanism_frame_iron", "CanopyMechanism_frame_wood"] },
  blockage: { path: "canopy_timed_blockage", required: ["CanopyMechanism_blockage_iron", "CanopyMechanism_blockage_rust"] },
  gate: { path: "canopy_relay_gate", required: ["CanopyMechanism_gate_iron", "CanopyMechanism_gate_brass"] },
  instrument: { path: "canopy_instrument_case", required: ["CanopyMechanism_case_iron", "CanopyMechanism_case_brass"] },
  bridge: { path: "canopy_gear_bridge", required: ["CanopyMechanism_bridge_top"] },
  lens: { path: "canopy_lens_cartridge", required: ["CanopyOptic_lens_surface", "CanopyOptic_lens_rim", "CanopyOptic_lens_retainer"] },
  receiver: { path: "canopy_lens_receiver", required: ["CanopyOptic_receiver_surface", "CanopyOptic_receiver_iron", "CanopyOptic_receiver_brass"] },
} as const;
type Kind = keyof typeof ASSETS;

/** A render skin follows its existing root; it never creates, moves or disposes
 * that root's body. Explicit visibility keeps a dormant bridge dormant even
 * when source arrives late; root disposal retires shattered/removed art. */
export class CanopyMechanismSkin {
  readonly ready: Promise<boolean>;
  private readonly artRoot: TransformNode;
  private readonly fallback: readonly AbstractMesh[];
  private readonly originalCasters: boolean[];
  private readonly originalSurface: VertexData | null;
  private readonly originalSurfaceCaster: boolean;
  private surface: AbstractMesh | null = null;
  private source: InstantiatedModel | null = null;
  private casters: AbstractMesh[] = [];
  private visible: boolean;
  private retired = false;
  private readonly observer;

  constructor(private readonly root: Mesh, private readonly shadows: ShadowGenerator,
    private readonly kind: Kind, private readonly options: { scale?: number; offset?: Vector3; visible?: boolean; fallbackMeshes?: readonly AbstractMesh[]; surfaceMesh?: string } = {}) {
    this.fallback = options.fallbackMeshes ?? [root]; this.visible = options.visible ?? root.isVisible;
    const list = shadows.getShadowMap()?.renderList ?? [];
    this.originalCasters = this.fallback.map(m => list.includes(m));
    this.originalSurface=options.surfaceMesh?VertexData.ExtractFromMesh(root,true,true):null;
    this.originalSurfaceCaster=list.includes(root);
    this.artRoot = new TransformNode(root.name + "_mechanismArt", root.getScene());
    this.artRoot.parent = root; this.artRoot.scaling.setAll(options.scale ?? 1); this.artRoot.setEnabled(false);
    if(options.offset)this.artRoot.position.copyFrom(options.offset);
    this.observer = root.onDisposeObservable.addOnce(() => this.dispose());
    this.applyVisibility(); this.ready = this.load();
  }

  private async load(): Promise<boolean> {
    let candidate: InstantiatedModel | null = null;
    try {
      const scene = this.root.getScene(), asset = ASSETS[this.kind];
      candidate = await getAssetManager().instantiateSharedModel(scene, "/assets/models/environment/" + asset.path + ".glb");
      if (this.retired || this.root.isDisposed() || scene.isDisposed) { candidate.dispose(); return false; }
      if (!candidate.rootNodes.length) throw new Error("Missing mechanism source root");
      const geometry = candidate.meshes.filter(m => m.getTotalVertices() > 0);
      const clean = (m: AbstractMesh): string => m.name.replace(/^(Clone of )+/, "");
      for (const name of asset.required) if (geometry.filter(m => clean(m) === name).length !== 1)
        throw new Error("Missing/duplicate mechanism part: " + name);
      if (geometry.some(m => !m.isEnabled() || !m.isVisible || m.visibility !== 1 || !m.material
        || m.material.alpha !== 1 || m.material.needAlphaBlendingForMesh(m) || m.material.needAlphaTestingForMesh(m)))
        throw new Error("Incomplete opaque mechanism source");
      if(this.options.surfaceMesh)this.adoptSurface(geometry,clean);
      for (const node of candidate.rootNodes) node.parent = this.artRoot;
      if (this.kind === "bridge") {
        const top = geometry.find(m => clean(m) === "CanopyMechanism_bridge_top")!;
        this.root.computeWorldMatrix(true); top.computeWorldMatrix(true);
        const a = top.getBoundingInfo().boundingBox, b = this.root.getBoundingInfo().boundingBox;
        const delta = Math.max(...a.minimumWorld.asArray().map((v,i) => Math.abs(v-b.minimumWorld.asArray()[i])),
          ...a.maximumWorld.asArray().map((v,i) => Math.abs(v-b.maximumWorld.asArray()[i])));
        if (delta > 1e-5) throw new Error("Bridge skin/body bounds differ: " + delta);
      }
      for (const mesh of geometry) { mesh.isPickable = false; mesh.receiveShadows = true; tagAs(mesh,"modular-final"); }
      this.source = candidate; this.applyVisibility(); return true;
    } catch (error) {
      candidate?.dispose();
      if (!this.retired && !this.root.getScene().isDisposed)
        console.warn("[Canopy] Mechanism art unavailable; retaining fallback:", this.kind, error);
      return false;
    }
  }

  /** Bake one authored surface into the original feedback/interaction mesh.
   * Material, root transform and local bounds remain owned by the puzzle.
   * Extracted data is copied; cached source geometry is never edited. */
  private adoptSurface(geometry:readonly AbstractMesh[],clean:(mesh:AbstractMesh)=>string):void {
    const matches=geometry.filter(mesh=>clean(mesh)===this.options.surfaceMesh);
    if(matches.length!==1)throw new Error("Missing/duplicate optical surface");
    const surface=matches[0];surface.computeWorldMatrix(true);
    const scale=this.options.scale??1,offset=this.options.offset??Vector3.ZeroReadOnly;
    const placement=Matrix.Scaling(scale,scale,scale).multiply(Matrix.Translation(offset.x,offset.y,offset.z));
    const data=VertexData.ExtractFromMesh(surface as Mesh,true,true),world=surface.getWorldMatrix().multiply(placement);
    if(!data.positions?.length||!data.normals?.length)throw new Error("Incomplete optical surface");
    const normals=Array.from(data.normals),normalMatrix=world.clone().invert().transpose();
    data.transform(world);
    // glTF quantization and placement may carry nonuniform scale. Normals
    // need the inverse transpose, not the position transform used above.
    data.normals=[];
    for(let i=0;i<normals.length;i+=3){
      const normal=Vector3.TransformNormal(Vector3.FromArray(normals,i),normalMatrix).normalize();
      data.normals.push(normal.x,normal.y,normal.z);
    }
    const bounds=this.root.getBoundingInfo().boundingBox;
    for(let axis=0;axis<3;axis++){
      const values=Array.from(data.positions).filter((_,i)=>i%3===axis);
      if(values.some(v=>!Number.isFinite(v))||Math.abs(Math.min(...values)-bounds.minimum.asArray()[axis])>1e-5
        ||Math.abs(Math.max(...values)-bounds.maximum.asArray()[axis])>1e-5)throw new Error("Optical surface changes interaction bounds");
    }
    data.applyToMesh(this.root);
    this.surface=surface;surface.isVisible=false;
  }

  setVisible(visible: boolean): void {
    if(this.retired)return;
    if(this.visible===visible){this.syncRenderFlags();return;}
    this.visible=visible;this.applyVisibility();
  }

  private syncRenderFlags():void {
    for(const m of this.fallback)m.isVisible=this.visible&&!this.source;
    this.artRoot.setEnabled(this.visible&&!!this.source);
    if(this.surface)this.root.isVisible=this.visible;
  }

  private applyVisibility(): void {
    for (const m of this.casters) this.shadows.removeShadowCaster(m, false);
    this.casters = [];
    for (const m of this.fallback) {
      this.shadows.removeShadowCaster(m, false);
    }
    this.syncRenderFlags();
    const selected = this.visible ? [ ...(this.surface?[this.root]:[]),
      ...(this.source?.meshes ?? this.fallback).filter(mesh=>mesh!==this.surface) ] : [];
    this.casters = selected.filter(m => m.getTotalVertices() > 0);
    for (const m of this.casters) this.shadows.addShadowCaster(m, false);
  }

  dispose(): void {
    if (this.retired) return; this.retired = true;
    this.root.onDisposeObservable.remove(this.observer);
    for (const m of this.casters) this.shadows.removeShadowCaster(m, false);
    if(this.surface&&!this.root.isDisposed()){
      this.originalSurface?.applyToMesh(this.root);
      if(this.originalSurfaceCaster&&!this.root.getScene().isDisposed)this.shadows.addShadowCaster(this.root,false);
    }
    this.casters = []; this.source?.dispose(); this.source = null; this.artRoot.dispose();
    this.fallback.forEach((m,i) => {
      if (m.isDisposed()) return;
      m.isVisible = this.visible;
      if (this.visible && this.originalCasters[i] && !m.getScene().isDisposed) this.shadows.addShadowCaster(m,false);
    });
  }
}
